import React, { FunctionComponent, useMemo } from 'react';
import styled from 'styled-components';
import Button from 'components/Button';
import { Body } from 'components/Card';
import Flex from 'components/Flex';
import Modal from 'components/Modal';
import Basic from 'components/Modal/Basic';
import Text from 'components/Text';
import useOutsidePopupToggle from 'hooks/useOutsidePopupToggle';
import useTheme from 'hooks/useTheme';
import useToggle from 'hooks/useToggle';
import { ReactComponent as QuestionIcon } from 'icons/question.svg';
import { ReactComponent as VideoTutorialsIcon } from 'icons/video-tuts.svg';
import { ReactComponent as DocTutorialsIcon } from 'icons/article.svg';
import { ReactComponent as SupportTutorialsIcon } from 'icons/mail.svg';
import HeaderNavMenuTemplate, {
  NavMenuLayout,
} from 'pages/App/Header/Menu/components/HeaderNavMenuTemplate';
import { IMenuItem } from 'pages/App/Header/Menu/UserMenu/useUserMenu';
import HeaderLinkTemplate, { IconContainer } from '../HeaderLinkTemplate';
import { useContactSupportModal } from 'templates/ContactSupport/Context';

interface IVideoTutorials {
  className?: string;
  pathToVideo?: string;
  pathToDoc?: string;
}

const VideoTutorialsHeaderLink: FunctionComponent<IVideoTutorials> = ({
  className = '',
  pathToVideo,
  pathToDoc,
}) => {
  const theme = useTheme();
  const [videoModalIsOpen, toggleVideoModal] = useToggle(false);
  const { toggle, isMenuOpen, nodeRef } = useOutsidePopupToggle<HTMLDivElement>();
  const { openModal: openContactModal } = useContactSupportModal();

  const items = useMemo((): IMenuItem[] => {
    const items = [];

    if (pathToVideo) {
      items.push({
        icon: <StyledVideoTutorialsIcon />,
        onClick: () => {
          toggle();
          toggleVideoModal();
        },
        title: 'Video Tutorial',
        visible: true,
      });
    }

    if (pathToDoc) {
      items.push({
        icon: <StyledDocTutorialsIcon />,
        onClick: () => {
          window.open(pathToDoc, '_blank')?.focus();
        },
        title: 'Help Guides & Articles',
        visible: true,
      });
    }

    items.push({
      icon: <StyledSupportTutorialsIcon />,
      onClick: () => {
        toggle();
        openContactModal('help_menu');
      },
      title: 'Contact CondoGenie Support',
      visible: true,
    });

    return items;
  }, [isMenuOpen, pathToDoc, pathToVideo]);

  const questionIcon = useMemo(
    () => (
      <IconContainer
        alignItems="center"
        justifyContent="center"
        backgroundColor={theme.colors.darkBlue}
      >
        <QuestionIcon />
      </IconContainer>
    ),
    []
  );

  return (
    <>
      {pathToVideo && (
        <Modal isOpen={videoModalIsOpen} close={toggleVideoModal} contentMaxWidth={800}>
          <Basic close={toggleVideoModal}>
            <VideoBody>
              <VideoBox
                frameBorder="0"
                allow="autoplay"
                allowFullScreen={true}
                scrolling="auto"
                src={pathToVideo}
              />
            </VideoBody>
          </Basic>
        </Modal>
      )}
      <HeaderLinkTemplate
        className={className}
        desktopView={
          <Button
            icon={questionIcon}
            onClick={toggle}
            className="ml16"
            styleType="blue"
            padding="3px 15px 3px 3px"
          >
            <Text size="xs" color={theme.colors.darkBlue} weight="bold">
              CondoGenie Help
            </Text>
          </Button>
        }
        mobileView={
          <ButtonTemplate onClick={toggle} className="ml4">
            {questionIcon}
          </ButtonTemplate>
        }
      >
        {isMenuOpen && (
          <HeaderNavMenuTemplate nodeRef={nodeRef} items={items} layout={StyledMenu} />
        )}
      </HeaderLinkTemplate>
    </>
  );
};

export default VideoTutorialsHeaderLink;

const VideoBody = styled(Body)`
  padding: 0 10px 10px 10px;
`;

const StyledVideoTutorialsIcon = styled(VideoTutorialsIcon)`
  width: 20px;
  height: 16px;
  path {
    fill: ${(props) => props.theme.colors.darkBlue} !important;
  }
`;

const StyledDocTutorialsIcon = styled(DocTutorialsIcon)`
  width: 20px;
  height: 16px;
  path {
    fill: ${(props) => props.theme.colors.darkBlue} !important;
  }
`;

const StyledSupportTutorialsIcon = styled(SupportTutorialsIcon)`
  width: 20px;
  height: 16px;
  path {
    fill: ${(props) => props.theme.colors.darkBlue} !important;
  }
`;

const VideoBox = styled.iframe`
  width: 100%;
  min-height: 415px;
`;

export const ButtonTemplate = styled(Flex)<{ onClick?: () => void }>`
  cursor: pointer;
`;

const StyledMenu = styled(NavMenuLayout)`
  margin-top: 0;
  width: 270px;
  top: 60px;
  ${(props) => props.theme.media.xs`
    right: 0px;
    top: 80px;
  `};
`;
